<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[100000] flex items-center justify-center p-2 sm:p-4 bg-black/60"
    @click="handleClickOutside"
  >
    <div
      class="relative flex flex-col w-full max-w-5xl bg-white shadow-2xl rounded-xl h-[92vh] overflow-hidden"
      @click.stop
    >
      <!-- Header -->
      <div
        class="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 shrink-0"
      >
        <div class="min-w-0">
          <h3
            class="text-base font-semibold text-gray-900 sm:text-lg line-clamp-1"
          >
            {{ dokumentdata.nama_dok || "Detail Dokumen" }}
          </h3>
          <div class="flex flex-wrap items-center gap-2 mt-1">
            <span
              v-if="dokumentdata.jenisdokumen?.nama"
              class="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full text-primary bg-primary/10"
            >
              {{ dokumentdata.jenisdokumen.nama }}
            </span>
            <span v-if="dokumentdata.publish" class="text-xs text-gray-400">
              {{ dokumentdata.publish }}
            </span>
          </div>

          <!-- Description (collapsible) -->
          <details v-if="dokumentdata.deskripsi" class="mt-2 group">
            <summary
              class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 cursor-pointer hover:text-primary"
            >
              <i
                class="text-[10px] transition-transform fa-solid fa-chevron-right group-open:rotate-90"
              ></i>
              Deskripsi
            </summary>
            <div
              class="p-3 mt-2 prose-sm prose rounded-lg bg-gray-50 max-w-none max-h-32 overflow-y-auto"
              v-html="dokumentdata.deskripsi"
            ></div>
          </details>
        </div>

        <button
          type="button"
          aria-label="Tutup"
          @click="closeModal"
          class="text-gray-400 transition shrink-0 hover:text-gray-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- File tabs (when more than one attachment) -->
      <div
        v-if="files.length > 1"
        class="flex gap-2 px-5 py-2 overflow-x-auto border-b border-gray-100 bg-gray-50 shrink-0"
      >
        <button
          v-for="(file, i) in files"
          :key="i"
          type="button"
          @click="activeIndex = i"
          :class="[
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-lg whitespace-nowrap transition-colors',
            i === activeIndex
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary',
          ]"
        >
          <i class="fa-regular fa-file"></i>
          Berkas {{ i + 1 }}
        </button>
      </div>

      <!-- Viewer -->
      <div class="relative flex-1 min-h-0 bg-gray-100">
        <iframe
          v-if="activeUrl && isPdf(activeUrl)"
          :src="`${activeUrl}#view=FitH`"
          class="w-full h-full"
          title="PDF Reader"
        ></iframe>

        <div
          v-else-if="activeUrl && isImage(activeUrl)"
          class="flex items-center justify-center w-full h-full p-4 overflow-auto"
        >
          <img
            :src="activeUrl"
            alt="Pratinjau dokumen"
            class="object-contain max-w-full max-h-full rounded shadow"
          />
        </div>

        <div
          v-else-if="activeUrl"
          class="flex flex-col items-center justify-center w-full h-full px-6 text-center"
        >
          <i class="mb-3 text-5xl text-gray-300 fa-regular fa-file-lines"></i>
          <p class="text-sm text-gray-500">
            Berkas ini tidak dapat dipratinjau di sini.
          </p>
          <a
            :href="activeUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 px-4 py-2 mt-4 text-sm font-semibold text-white rounded-lg bg-primary hover:bg-primary-700"
          >
            <i class="fa-solid fa-download"></i>
            Unduh berkas
          </a>
        </div>

        <div
          v-else
          class="flex flex-col items-center justify-center w-full h-full px-6 text-center"
        >
          <i class="mb-3 text-5xl text-gray-300 fa-regular fa-folder-open"></i>
          <p class="text-sm text-gray-500">
            Tidak ada lampiran pada dokumen ini.
          </p>
        </div>
      </div>

      <!-- Toolbar -->
      <div
        v-if="activeUrl"
        class="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 shrink-0"
      >
        <a
          :href="activeUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary"
        >
          <i class="fa-solid fa-up-right-from-square"></i>
          Buka di tab baru
        </a>
        <a
          :href="activeUrl"
          download
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg bg-primary hover:bg-primary-700"
        >
          <i class="fa-solid fa-download"></i>
          Unduh
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch } from "vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Detail Dokumen",
  },
  dokumendata: {
    type: [Array, Object],
    required: true,
  },
});

const emit = defineEmits(["update:isOpen"]);

const closeModal = () => emit("update:isOpen", false);

const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

const dokumentdata = computed(() =>
  Array.isArray(props.dokumendata)
    ? props.dokumendata[0] || {}
    : props.dokumendata || {}
);

const files = computed(() => {
  const fp = dokumentdata.value.file_path;
  return Array.isArray(fp) ? fp.filter(Boolean) : fp ? [fp] : [];
});

const activeIndex = ref(0);

// Normalize a stored path into a usable URL.
const fileUrl = (file) => {
  if (!file) return "";
  if (/^https?:\/\//.test(file) || file.startsWith("/")) return file;
  return "/storage/" + file;
};

const activeUrl = computed(() => fileUrl(files.value[activeIndex.value]));

function isImage(filePath) {
  return /\.(jpg|jpeg|png|gif|bmp|webp)(\?.*)?$/i.test(filePath);
}

function isPdf(filePath) {
  return /\.pdf(\?.*)?$/i.test(filePath);
}

// When the modal opens (or the document changes), select the first PDF if any.
const pickDefault = () => {
  const idx = files.value.findIndex((f) => isPdf(f));
  activeIndex.value = idx >= 0 ? idx : 0;
};

watch(
  () => props.isOpen,
  (open) => {
    if (open) pickDefault();
  }
);
watch(() => props.dokumendata, pickDefault, { deep: true });
</script>

<style scoped>
.prose ul {
  list-style: disc;
  padding-left: 1.25rem;
}
.prose ol {
  list-style: decimal;
  padding-left: 1.25rem;
}
.prose p {
  margin-bottom: 0.75rem;
}
.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}
</style>
